import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useChangePassword } from "../hooks/useChangePassword";
import { useChangeSecurityQ } from "../hooks/useChangeSecurityQ";
import { useChangeSecurityA } from "../hooks/useChangeSecurityA";
import { Banner } from "../components/Banner";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";
import { ApiError } from "../api/client";

const SECURITY_QUESTION_CODES = [
  "SECURITY_Q_PET_NAME",
  "SECURITY_Q_BIRTH_CITY",
  "SECURITY_Q_MOTHER_MAIDEN_NAME",
  "SECURITY_Q_PRIMARY_SCHOOL",
  "SECURITY_Q_CHILDHOOD_STREET",
];

/**
 * Account & Security page (PMAUTH-8.3).
 * Three sections: change-password, change-security-q, change-security-a.
 * Each requires current_password. Success invalidates pm-session → PMOnly guard
 * → redirect to /login on next probe.
 */
export function Account() {
  const intl = useIntl();
  const navigate = useNavigate();

  // Change password
  const changePassword = useChangePassword();
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpClientError, setCpClientError] = useState<string | null>(null);

  // Change security question
  const changeSecurityQ = useChangeSecurityQ();
  const [cqCurrent, setCqCurrent] = useState("");
  const [cqNewCode, setCqNewCode] = useState(SECURITY_QUESTION_CODES[0]);

  // Change security answer
  const changeSecurityA = useChangeSecurityA();
  const [caCurrent, setCaCurrent] = useState("");
  const [caNewAnswer, setCaNewAnswer] = useState("");

  const getErrorMessage = (err: ApiError | null) => {
    if (!err) return null;
    return intl.formatMessage({ id: err.code, defaultMessage: err.code });
  };

  const handleChangePassword = (e: FormEvent) => {
    e.preventDefault();
    setCpClientError(null);
    if (cpNew.length < 7) {
      setCpClientError(intl.formatMessage({ id: "ERR_PASSWORD_TOO_SHORT" }));
      return;
    }
    if (cpNew !== cpConfirm) {
      setCpClientError(intl.formatMessage({ id: "ERR_PASSWORD_MISMATCH" }));
      return;
    }
    changePassword.mutate(
      { current_password: cpCurrent, new_password: cpNew },
      {
        onSuccess: () =>
          navigate("/login", {
            replace: true,
            state: { banner: intl.formatMessage({ id: "OK_PM_PASSWORD_CHANGED" }) },
          }),
      },
    );
  };

  const handleChangeSecurityQ = (e: FormEvent) => {
    e.preventDefault();
    changeSecurityQ.mutate(
      { current_password: cqCurrent, new_security_question_code: cqNewCode },
      {
        onSuccess: () =>
          navigate("/login", {
            replace: true,
            state: { banner: intl.formatMessage({ id: "OK_PM_SECURITY_Q_CHANGED" }) },
          }),
      },
    );
  };

  const handleChangeSecurityA = (e: FormEvent) => {
    e.preventDefault();
    changeSecurityA.mutate(
      { current_password: caCurrent, new_security_answer: caNewAnswer },
      {
        onSuccess: () =>
          navigate("/login", {
            replace: true,
            state: { banner: intl.formatMessage({ id: "OK_PM_SECURITY_A_CHANGED" }) },
          }),
      },
    );
  };

  return (
    <div data-testid="account-page" className="ps-8 pe-8 pbs-8 pbe-8">
      <h1 className="text-2xl font-semibold mbe-8">
        <FormattedMessage id="LABEL_ACCOUNT_SECURITY" />
      </h1>

      {/* Change password section */}
      <section className="bg-surface-raised border border-divider rounded-md p-6 mbe-6">
        <h2 className="text-2xl font-semibold mbe-4">
          <FormattedMessage id="LABEL_CHANGE_PASSWORD" />
        </h2>
        <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
          <TextField
            id="cp-current"
            label={intl.formatMessage({ id: "LABEL_CURRENT_PASSWORD" })}
            type="password"
            value={cpCurrent}
            onChange={(v) => { setCpCurrent(v); changePassword.reset(); setCpClientError(null); }}
            autoComplete="current-password"
            data-testid="cp-current-password"
          />
          <TextField
            id="cp-new"
            label={intl.formatMessage({ id: "LABEL_NEW_PASSWORD" })}
            type="password"
            value={cpNew}
            onChange={(v) => { setCpNew(v); changePassword.reset(); setCpClientError(null); }}
            autoComplete="new-password"
            data-testid="cp-new-password"
          />
          <TextField
            id="cp-confirm"
            label={intl.formatMessage({ id: "LABEL_CONFIRM_PASSWORD" })}
            type="password"
            value={cpConfirm}
            onChange={(v) => { setCpConfirm(v); changePassword.reset(); setCpClientError(null); }}
            autoComplete="new-password"
            data-testid="cp-confirm-password"
          />
          {(cpClientError || changePassword.error) && (
            <Banner variant="error" data-testid="cp-error-banner">
              {cpClientError ?? getErrorMessage(changePassword.error)}
            </Banner>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={changePassword.isPending}
              data-testid="cp-save-button"
            >
              <FormattedMessage id="LABEL_SAVE_NEW_PASSWORD" />
            </Button>
          </div>
        </form>
      </section>

      {/* Change security question section */}
      <section className="bg-surface-raised border border-divider rounded-md p-6 mbe-6">
        <h2 className="text-2xl font-semibold mbe-4">
          <FormattedMessage id="LABEL_CHANGE_SECURITY_Q" />
        </h2>
        <form onSubmit={handleChangeSecurityQ} className="flex flex-col gap-4">
          <TextField
            id="cq-current"
            label={intl.formatMessage({ id: "LABEL_CURRENT_PASSWORD" })}
            type="password"
            value={cqCurrent}
            onChange={(v) => { setCqCurrent(v); changeSecurityQ.reset(); }}
            autoComplete="current-password"
            data-testid="cq-current-password"
          />
          <div className="flex flex-col gap-1">
            <label
              htmlFor="cq-new-question"
              className="text-sm font-semibold text-on-surface"
            >
              <FormattedMessage id="LABEL_NEW_SECURITY_Q" />
            </label>
            <select
              id="cq-new-question"
              value={cqNewCode}
              onChange={(e) => setCqNewCode(e.target.value)}
              className="h-10 w-full bg-surface-raised border border-divider rounded-md ps-3 pe-3 text-base focus-visible:ring-2 focus-visible:ring-primary outline-none transition-colors duration-200"
              data-testid="cq-new-question-select"
            >
              {SECURITY_QUESTION_CODES.map((code) => (
                <option key={code} value={code}>
                  {intl.formatMessage({ id: code, defaultMessage: code })}
                </option>
              ))}
            </select>
          </div>
          {changeSecurityQ.error && (
            <Banner variant="error" data-testid="cq-error-banner">
              {getErrorMessage(changeSecurityQ.error)}
            </Banner>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={changeSecurityQ.isPending}
              data-testid="cq-save-button"
            >
              <FormattedMessage id="LABEL_SAVE_SECURITY_Q" />
            </Button>
          </div>
        </form>
      </section>

      {/* Change security answer section */}
      <section className="bg-surface-raised border border-divider rounded-md p-6 mbe-6">
        <h2 className="text-2xl font-semibold mbe-4">
          <FormattedMessage id="LABEL_CHANGE_SECURITY_A" />
        </h2>
        <form onSubmit={handleChangeSecurityA} className="flex flex-col gap-4">
          <TextField
            id="ca-current"
            label={intl.formatMessage({ id: "LABEL_CURRENT_PASSWORD" })}
            type="password"
            value={caCurrent}
            onChange={(v) => { setCaCurrent(v); changeSecurityA.reset(); }}
            autoComplete="current-password"
            data-testid="ca-current-password"
          />
          <TextField
            id="ca-new-answer"
            label={intl.formatMessage({ id: "LABEL_NEW_SECURITY_A" })}
            type="text"
            value={caNewAnswer}
            onChange={(v) => { setCaNewAnswer(v); changeSecurityA.reset(); }}
            helperText={intl.formatMessage({ id: "LABEL_SECURITY_A_HELPER" })}
            data-testid="ca-new-answer"
          />
          {changeSecurityA.error && (
            <Banner variant="error" data-testid="ca-error-banner">
              {getErrorMessage(changeSecurityA.error)}
            </Banner>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={changeSecurityA.isPending}
              data-testid="ca-save-button"
            >
              <FormattedMessage id="LABEL_SAVE_SECURITY_A" />
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
